import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Trophy, Award } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AggregatePeriod, useAggregateLeaderboard } from '@/api/query/useLeaderboard';
import { useUser } from '@/hooks/useUser';
import { useAuth } from '@/contexts/AuthContext';
import { UserPickemsList } from './UserPickemsList';
import LeaderboardRowSkeleton from './LeaderboardRowSkeleton';
import { WeekFilter } from './WeekFilter';
import { useWeeks } from './WeekFilter/useWeeks';

interface LeaderboardSectionProps {
  title?: string;
  className?: string;
}

const LeaderboardSection: React.FC<LeaderboardSectionProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const { iframeParams } = useAuth();

  // Weeks for current ISO week-of-year (Week 1..currentWeek)
  const { weeks, latestWeekOffset, currentISOWeek } = useWeeks();

  const [period, setPeriod] = useState<AggregatePeriod>('week');

  const tabs: Array<{ id: AggregatePeriod; label: string }> = [
    { id: 'week', label: t('leaderboard.week') },
    { id: 'month', label: t('leaderboard.month') },
    { id: 'year', label: t('leaderboard.year') },
  ];

  // selectedWeek is a *week index* (0-based), representing Week (index+1)
  const [selectedWeek, setSelectedWeek] = useState(latestWeekOffset);

  const now = useMemo(() => new Date(), []);
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1..12
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // TODO: i18n!
  const months = useMemo(
    () => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    []
  );

  const leagueCode = iframeParams.location ?? 'ca';

  const weekOffset = useMemo(() => {
    const selectedWeekNumber = selectedWeek + 1;
    return currentISOWeek - selectedWeekNumber;
  }, [selectedWeek, currentISOWeek]);

  const isFutureWeek = weekOffset < 0;

  const params = useMemo(() => {
    if (period === 'week') {
      return { period: 'week', weekOffset, leagueCode } as const;
    }
    if (period === 'month') {
      return { period: 'month', month: selectedMonth, year: selectedYear, leagueCode } as const;
    }
    return { period: 'year', leagueCode } as const;
  }, [period, weekOffset, selectedMonth, selectedYear, leagueCode]);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAggregateLeaderboard(params, { enabled: period !== 'week' || !isFutureWeek });

  const { leaderboard, currentUserEntry } = useMemo(() => {
    // For future weeks, always show no data
    if (period === 'week' && isFutureWeek) {
      return { leaderboard: [], currentUserEntry: null };
    }

    if (!data?.pages) return { leaderboard: [], currentUserEntry: null };

    const allEntries = data.pages.flatMap(page => page.leaderboard);
    if (allEntries.length === 0) return { leaderboard: [], currentUserEntry: null };

    const firstPlacePoints = allEntries.find(e => e.rank === 1)?.total_points;
    const tiedFirstPlace = allEntries.filter(e => e.total_points === firstPlacePoints).length > 1;

    const userEntry = currentUser ? allEntries.find(e => e.user_id === currentUser.id) : null;

    const entries = allEntries.map(entry => ({
      rank: entry.rank,
      user_id: entry.user_id,
      username: entry.username,
      points: entry.total_points,
      isCurrentUser: currentUser?.id === entry.user_id,
      isWinner: entry.rank === 1,
      isAbsoluteWinner: entry.rank === 1 && tiedFirstPlace,
      pickems_data: entry.pickems_data,
    }));

    return {
      leaderboard: entries,
      currentUserEntry: userEntry
        ? {
            rank: userEntry.rank,
            user_id: userEntry.user_id,
            username: userEntry.username,
            points: userEntry.total_points,
            isCurrentUser: true,
            isWinner: userEntry.rank === 1,
            isAbsoluteWinner: userEntry.rank === 1 && tiedFirstPlace,
            pickems_data: userEntry.pickems_data,
          }
        : null,
    };
  }, [data, currentUser, period, isFutureWeek]);

  const displayLeaderboard = useMemo(() => {
    if (!leaderboard.length) return leaderboard;
    if (!currentUserEntry) return leaderboard;

    return [currentUserEntry, ...leaderboard.filter(e => e.user_id !== currentUserEntry.user_id)];
  }, [leaderboard, currentUserEntry]);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleTabChange = (next: AggregatePeriod) => {
    setPeriod(next);

    if (next === 'week') {
      setSelectedWeek(latestWeekOffset);
    }

    if (next === 'month') {
      const d = new Date();
      setSelectedMonth(d.getMonth() + 1);
      setSelectedYear(d.getFullYear());
    }
  };

  const contentKey = useMemo(() => {
    if (period === 'week') return `${period}:${selectedWeek}:${leagueCode ?? ''}`;
    if (period === 'month') return `${period}:${selectedYear}-${selectedMonth}:${leagueCode ?? ''}`;
    return `${period}:all:${leagueCode ?? ''}`;
  }, [period, selectedWeek, selectedMonth, selectedYear, leagueCode]);

  return (
    <div data-test-id="leaderboard-section" className={`${className}`}>
      {/* Tab Navigation */}
      <div className="mb-4">
        <div className="flex space-x-1 bg-app-surface rounded-xl p-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-test-id={`leaderboard-period-${tab.id}`}
              onClick={() => handleTabChange(tab.id)}
              className={`
                flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-smooth
                ${
                  period === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'bg-black/20 text-text-secondary hover:text-text-primary'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Week Selection */}
      {period === 'week' && (
        <WeekFilter weeks={weeks} selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek} />
      )}

      {/* Month Selection */}
      {period === 'month' && (
        <div className="mb-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {months.map((label, idx) => {
              const monthNum = idx + 1; // 1..12
              return (
                <button
                  key={monthNum}
                  data-test-id={`leaderboard-month-${monthNum}`}
                  onClick={() => setSelectedMonth(monthNum)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-smooth
                    ${
                      selectedMonth === monthNum
                        ? 'bg-white text-gray-900'
                        : 'bg-black/20 text-text-secondary hover:text-text-primary'
                    }
                  `}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Leaderboard Header */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-text-secondary text-sm font-medium px-2">
          <div className="flex items-center gap-4">
            <span>{t('leaderboard.rank')}</span>
            <span>{t('leaderboard.user')}</span>
          </div>
          <span>{t('leaderboard.points')}</span>
        </div>
      </div>

      {/* Leaderboard Content */}
      <div key={contentKey}>
        {isLoading && (
          <div className="space-y-2">
            {[...Array(15)].map((_, index) => (
              <motion.div
                key={`skeleton-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.3 }}
              >
                <LeaderboardRowSkeleton />
              </motion.div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-center text-red-500 py-8">{t('leaderboard.failedToLoad')}</div>
        )}

        {!isLoading && !error && leaderboard.length === 0 && (
          <div className="text-center text-text-secondary py-8">{t('leaderboard.noData')}</div>
        )}

        {!isLoading && !error && leaderboard.length > 0 && (
          <TooltipProvider>
            {/* Leaderboard List */}
            <Accordion type="single" collapsible className="space-y-2">
              <AnimatePresence mode="sync">
                {displayLeaderboard.map((player, index) => (
                  <motion.div
                    key={player.user_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: Math.min(index * 0.01, 0.3), duration: 0.3 }}
                  >
                    <AccordionItem
                      value={`player-${player.user_id}`}
                      data-test-id={`leaderboard-row-${player.user_id}`}
                      data-test-rank={player.rank}
                      data-test-is-current-user={player.isCurrentUser}
                      className={`
                        rounded-xl border-0 overflow-hidden
                        ${player.isCurrentUser ? 'bg-bet99-primary text-white' : 'bg-game-card'}
                      `}
                    >
                      <AccordionTrigger
                        className={`
                          px-4 py-3 hover:no-underline
                          ${
                            player.isCurrentUser
                              ? 'text-white hover:bg-bet99-primary-dark'
                              : 'text-text-primary hover:bg-game-card-active'
                          }
                        `}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-4">
                            <span className="font-bold text-lg">{player.rank}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{player.username}</span>
                              {player.isWinner && (
                                <Trophy
                                  data-test-id="trophy-icon"
                                  className="w-4 h-4 text-yellow-400"
                                />
                              )}
                              {player.isAbsoluteWinner && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Award
                                      className={`w-4 h-4 ${
                                        player.isCurrentUser ? 'text-white' : 'text-bet99-primary'
                                      }`}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('leaderboard.absoluteWinner')}</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                          <span data-test-id="points-display" className="font-bold text-lg">
                            {player.points}
                          </span>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent>
                        <div
                          className={`px-4 ${player.isCurrentUser ? 'text-white' : 'text-text-secondary'}`}
                        >
                          <div className="text-sm">
                            <div
                              className={`grid grid-cols-3 gap-2 font-medium mb-2 pb-2 border-b ${
                                player.isCurrentUser ? 'border-white/20' : 'border-border/20'
                              }`}
                            >
                              <span>{t('leaderboard.pickem')}</span>
                              <span className="text-center">{t('leaderboard.rank')}</span>
                              <span className="text-right">{t('leaderboard.points')}</span>
                            </div>
                            <UserPickemsList
                              userId={player.user_id}
                              period={period}
                              weekOffset={period === 'week' ? weekOffset : 0}
                              leagueCode={leagueCode}
                              isCurrentUser={player.isCurrentUser}
                            />
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                ))}
              </AnimatePresence>
            </Accordion>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
};

export default LeaderboardSection;
